import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { makeStyles } from '@material-ui/styles';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { DocsProvider } from '../../context/DocsContext';
import theme from '../../themes/theme';
import { axiosInstance, cancelToken } from '../../utils/axiosInstance';
import { endpoint, packagePreviewStrings, packageStatusLabels } from '../../utils/constants';
import { convertToPolish, errorHandling } from '../../utils/helpers';
import TableReports from '../Tables/TableReports';

const DEFAULT_CHUNK = 50;

/**
 * A value the preview could not recover is shown as an em dash, never as a
 * plausible-looking guess.
 */
const formatValue = value =>
    value === null || value === undefined || value === ''
        ? packagePreviewStrings.VALUE_UNKNOWN
        : String(value);

const useStyles = makeStyles({
    root: {
        width: '100%',
    },
    state: {
        marginTop: '1.1rem',
        padding: '2rem',
        fontSize: '1rem',
    },
    error: {
        marginTop: '1.1rem',
        border: `1px solid ${theme.colors.border}`,
        borderLeft: `4px solid ${theme.colors.error}`,
        background: theme.colors.panelBackground,
        padding: '1.5rem 2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        maxWidth: 760,
    },
    errorHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    },
    errorIcon: {
        color: theme.colors.error,
    },
    errorTitle: {
        margin: 0,
        fontSize: '1.15rem',
        fontWeight: theme.font.weight.bold,
    },
    errorReason: {
        margin: 0,
        fontSize: '1rem',
    },
    errorMeta: {
        margin: 0,
        fontSize: '0.8rem',
        color: '#6b7280',
    },
    errorBack: {
        color: theme.colors.link,
        fontWeight: theme.font.weight.bold,
        textDecoration: 'none',
    },
    payload: {
        borderTop: `1px solid ${theme.colors.border}`,
        paddingTop: '0.75rem',
        width: '100%',
    },
    payloadTitle: {
        margin: '0 0 0.25rem',
        fontWeight: theme.font.weight.bold,
    },
    payloadData: {
        margin: '0.5rem 0 0',
        padding: '0.5rem',
        maxHeight: 240,
        overflow: 'auto',
        background: theme.colors.panelBackground,
        border: `1px solid ${theme.colors.border}`,
        fontSize: '0.75rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
    },
});

const PackagePreview = props => {
    const packageId = props?.match?.params?.id;
    const classes = useStyles();
    const [cancelHttpReq] = useState(cancelToken.source());
    const [options, setOptions] = useState({ family: '', chunk: DEFAULT_CHUNK });
    const [payload, setPayload] = useState(null);
    const [content, setContent] = useState({ data: [], headers: {} });
    const [isLoading, setIsLoading] = useState(false);
    const [notPreviewable, setNotPreviewable] = useState(null);
    const [isServerError, setIsServerError] = useState(false);
    const [loadedPackageId, setLoadedPackageId] = useState(packageId);

    // The router reuses this component between /package-preview/:id routes, so
    // reset the per-package state when the id changes.
    if (loadedPackageId !== packageId) {
        setLoadedPackageId(packageId);
        setOptions({ family: '', chunk: DEFAULT_CHUNK });
        setPayload(null);
    }

    const url = `${endpoint.PACKAGE_PREVIEW}/${packageId}/preview-documents`;

    useEffect(() => {
        getDocsData();
    }, [options.family, packageId]);

    useEffect(
        () => () => {
            cancelHttpReq.cancel();
            axiosInstance.delete(url).catch(() => {});
        },
        [url]
    );

    const getDocsData = () => {
        setIsLoading(true);
        setIsServerError(false);

        const fetchChunk = (page, chunk) =>
            axiosInstance.get(url, {
                params: {
                    ...(options.family && { family: options.family }),
                    page,
                    chunk,
                },
                cancelToken: cancelHttpReq.token,
            });

        fetchChunk(1, DEFAULT_CHUNK)
            .then(first => {
                const total = first.data?.total ?? 0;

                if (total <= DEFAULT_CHUNK) {
                    return first;
                }

                // The preview paginates in the browser, so once the total is
                // known the whole family is fetched in one page.
                return fetchChunk(1, total);
            })
            .then(resp => {
                if (!resp.data?.headers) {
                    return;
                }

                setNotPreviewable(null);
                setPayload(resp.data);
                setContent({
                    data: resp.data.data ?? [],
                    headers: resp.data.headers,
                    failedColumns: resp.data.failed_columns ?? {},
                });

                if (!options.family && resp.data.family) {
                    setOptions(prev => ({ ...prev, family: resp.data.family }));
                }
            })
            .catch(err => {
                if (err.message === undefined) {
                    return;
                }
                if (err.response?.status === 422) {
                    setNotPreviewable(err.response.data);
                    return;
                }
                if (err.response?.status === 404) {
                    setNotPreviewable({ message: 'Nie znaleziono pakietu.' });
                    return;
                }
                setIsServerError(true);
                errorHandling(err);
            })
            .finally(() => setIsLoading(false));
    };

    const changePaginationAndChunkSize = event =>
        setOptions(prev => ({ ...prev, chunk: event.target.value }));

    const providerProps = {
        options: [],
        handleFilter: () => {},
        clearedSearchField: () => {},
        clearSearchField: false,
        handleSearchCommunicator: () => {},
        handleSearchPackageId: () => {},
        handleResetFieldsButton: () => {},
        changePageHandler: () => {},
        reportType: payload?.family ?? options.family,
        loadData: setOptions,
        changePaginationAndChunkSize,
        chunk: options.chunk,
        searchCommunicator: '',
        searchPackageId: '',
        setIsLoading,
    };

    const status = payload?.package?.status;
    const statusLabel = status ? (packageStatusLabels[status] ?? status) : '';
    const headerText = payload?.family
        ? `${convertToPolish(payload.family)} - Wodociąg (${
              payload.package?.waterpipe?.name ?? ''
          })${statusLabel ? ` - ${statusLabel}` : ''}`
        : '';

    if (notPreviewable) {
        return (
            <div className={classes.error} data-cy="preview-not-previewable">
                <div className={classes.errorHeader}>
                    <ErrorOutlineIcon className={classes.errorIcon} />
                    <h2 className={classes.errorTitle}>{packagePreviewStrings.NOT_PREVIEWABLE_TITLE}</h2>
                </div>
                <p className={classes.errorReason}>
                    {notPreviewable.message ?? packagePreviewStrings.NOT_PREVIEWABLE}
                </p>
                <p className={classes.errorMeta}>
                    {packagePreviewStrings.PACKAGE_ID}: {packageId}
                </p>
                {notPreviewable.payload?.data !== undefined && (
                    <div className={classes.payload} data-cy="preview-raw-payload-block">
                        <p className={classes.payloadTitle}>{packagePreviewStrings.RAW_PAYLOAD_TITLE}</p>
                        <p className={classes.errorMeta}>
                            {packagePreviewStrings.RAW_PAYLOAD_LENGTH}: {notPreviewable.payload.length}
                            {notPreviewable.payload.truncated
                                ? ` (${packagePreviewStrings.RAW_PAYLOAD_TRUNCATED})`
                                : ''}
                        </p>
                        <pre className={classes.payloadData} data-cy="preview-raw-payload">
                            {notPreviewable.payload.data}
                        </pre>
                    </div>
                )}
                <Link className={classes.errorBack} to="/packages">
                    {packagePreviewStrings.BACK}
                </Link>
            </div>
        );
    }

    return (
        <DocsProvider value={providerProps}>
            <div className={classes.root}>
                {payload && !payload.total && payload.parse?.result !== 'failed' && (
                    <div className={classes.state} data-cy="preview-empty">
                        {packagePreviewStrings.EMPTY}
                    </div>
                )}

                <TableReports
                    headerText={headerText}
                    content={content}
                    isServerError={isServerError}
                    isComponentMounted={isLoading}
                    match={{ params: {} }}
                    hidePackageIdField
                    hideCommunicatorField
                    hideReportTypeField
                    hideResetButton
                    hideCountField
                    clientPagination
                    panelNote={`${packagePreviewStrings.NUMBER}: ${formatValue(
                        payload?.package?.communicator
                    )}`}
                />
            </div>
        </DocsProvider>
    );
};

export default PackagePreview;
